import Image from 'next/image'
import styled from 'styled-components'
import startWithAnySpaceImage from '../../../assets/ai-studio-landing/start_with_any_space.png'
import chooseAnyStyleImage from '../../../assets/ai-studio-landing/choose_any_style.png'
import controlColorsImage from '../../../assets/ai-studio-landing/control_colors.png'

const SectionWrapper = styled.section`
  width: 100%;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`

const SectionTitle = styled.h2`
  color: black;
  font-size: 48px;
  font-weight: 500;
  text-align: center;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const SectionSubtitle = styled.p`
  color: #3F3F3F;
  font-size: 20px;
  font-weight: 400;
  text-align: center;
  margin: 0 0 40px;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const CardsContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`

const FeatureCard = styled.div`
  flex: 1;
  max-width: 382px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 1024px) {
    max-width: 500px;
    width: 100%;
  }
`

const CardVisual = styled.div`
  width: 100%;
  height: 413px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: #f5f5f5;

  @media (max-width: 768px) {
    height: 300px;
  }
`

const CardTitle = styled.h3`
  color: black;
  font-size: 24px;
  font-weight: 500;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const CardDescription = styled.p`
  color: #545454;
  font-size: 18px;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const ActionButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 322px;
  height: 50px;
  background: linear-gradient(270deg, #FF996E 0%, #FF5565 37%, #FF4576 75%, #FF5E84 100%);
  border-radius: 100px;
  color: white;
  font-size: 18px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }
`

const CreativeControlSection = ({ onActionClick }) => {
  return (
    <SectionWrapper>
      <SectionTitle>Full Creative Control with AI Room Designer.</SectionTitle>
      <SectionSubtitle>
        Choose the style, color palette, materials, and mood — then let AI design your room.
      </SectionSubtitle>

      <CardsContainer>
        <FeatureCard>
          <CardVisual>
            <Image
              src={startWithAnySpaceImage}
              alt="Start with Any Space"
              layout="fill"
              objectFit="cover"
            />
          </CardVisual>
          <CardTitle>Start with Any Space</CardTitle>
          <CardDescription>
            Upload your real room, use a template, or start from a blank canvas. AI designs for real layouts and homes.
          </CardDescription>
        </FeatureCard>

        <FeatureCard>
          <CardVisual>
            <Image
              src={chooseAnyStyleImage}
              alt="Choose Any Style or Trend"
              layout="fill"
              objectFit="cover"
            />
          </CardVisual>
          <CardTitle>Choose Any Style or Trend</CardTitle>
          <CardDescription>
            Pick modern, boho, mid-century, minimal, maximal, and more — plus any room type for instant design direction.
          </CardDescription>
        </FeatureCard>

        <FeatureCard>
          <CardVisual>
            <Image
              src={controlColorsImage}
              alt="Control Colors, Materials & Lighting"
              layout="fill"
              objectFit="cover"
            />
          </CardVisual>
          <CardTitle>Control Colors, Materials & Lighting</CardTitle>
          <CardDescription>
            Set your palette, finishes, textures, and lighting — from warm daylight to cozy night — for the exact look you want.
          </CardDescription>
        </FeatureCard>
      </CardsContainer>

      <ActionButton onClick={onActionClick}>Try AI Room Designer</ActionButton>
    </SectionWrapper>
  )
}

export default CreativeControlSection
